/* eslint-disable react/no-array-index-key */
/* eslint-disable no-nested-ternary */
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import Dropdown from 'rc-dropdown';
import Menu, { Item as MenuItem } from 'rc-menu';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import PropTypes from 'prop-types';
import 'rc-dropdown/assets/index.css';
import Text from '../../../../shared/components/Text/Text';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import { setIsStaticAddColumnDropdownMenuOpen, setActiveModal } from '../../ducks/calendarActions';

const DropdownIcon = styled('div')`
  padding: 4px;
  background: #444444;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
`;

const DropdownContainerStatic = styled('div')`
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const MenuWrapper = styled('div')`
  display: flex;
  overflow: hidden;
  flex-direction: column;
`;

const CustomDivider = styled.div`
  height: 1px;
  margin: 4px 4;
  background-color: #EEEEEE;
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  border-radius: 4px;
  width: 100%;
`;

const DropdownHeader = styled('div')`
  background-color: white;
  padding-bottom: 8px;
  display: flex;
  justify-content: space-between;
  padding: 7px;
`;

const ArrowDiv = styled('div')`
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
`;

const MenuText = styled('div')`
  font-family: 'Nunito Sans';
  color: #444444;
  margin-bottom: 0px;
  font-size: 16px;
`;

const AddPropContainer = styled('div')`
  font-family: 'Nunito Sans';
  color: #444444;
  margin-bottom: 0px;
  font-size: 16px;
`;

// Create a new HoverableMenuItem component similar to CalendarItemDropdownOptions
const HoverableMenuItem = ({ item, onClick }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <MenuItem
      style={{
        paddingTop: 7,
        paddingBottom: 7,
        paddingLeft: 14,
        paddingRight: 7,
        cursor: 'pointer',
        background: isHovered ? '#F5F5F5' : 'transparent',
      }}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <IconContainer>
        {item.icon ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <IcomoonReact
              iconSet={iconSet}
              size={18}
              icon={item.icon}
              color='#9E9E9E'
              style={{ marginRight: 5 }}
            />
            <MenuText>{item.label}</MenuText>
          </div>
        ) : (
          <ArrowDiv>
            <MenuText>{item.label}</MenuText>
          </ArrowDiv>
        )}
      </IconContainer>
    </MenuItem>
  );
};

HoverableMenuItem.propTypes = {
  item: PropTypes.shape({
    label: PropTypes.string.isRequired,
    icon: PropTypes.string,
  }).isRequired,
  onClick: PropTypes.func.isRequired,
};

const capWords = (str) => str
  .split(' ')
  .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
  .join(' ');

const StaticAddColumnDropdownContainer = ({
  size,
  primaryRows,
  primaryRowSwitch,
  setPrimaryRowSwitch,
  setIntensityUnit,
  setPrimaryRowSwitchState,
}) => {
  const dispatch = useDispatch();
  const handleMenuItemClick = (row, e) => {
    e.domEvent.stopPropagation();
    const flippedStatus = !primaryRowSwitch[row.name];
    const newSwitch = { ...primaryRowSwitch };
    newSwitch[row.name] = flippedStatus;

    if (row.name === 'intensity (bpm)') {
      setIntensityUnit('bpm');
    }
    if (!flippedStatus) {
      setPrimaryRowSwitch(newSwitch);
    } else if (row.name === 'peak power' || row.name === 'velocity') {
      setPrimaryRowSwitch(newSwitch);
    } else {
      setPrimaryRowSwitchState(newSwitch);
    }
  };

  const handleAboutPropertiesClick = (e) => {
    // e.domEvent.stopPropagation();
    dispatch(setActiveModal('aboutExercisePropertiesModal'));
  };

  const menu = (
    <Menu
      style={{
        width: 280,
        minHeight: 100,
        overflowY: 'auto', /** Scroll when the content exceeds the height */
        marginBottom: '0px',
        border: '1px solid #eeeeee',
      }}
    >
      <AddPropContainer onClick={(e) => e.stopPropagation()}>
        <DropdownHeader>
          <Text fontWeight={700} fontSize='16px'>
            Add Property
          </Text>
          <Text fontSize='16px' color='#9E9E9E'>
            ⌘+P
          </Text>

        </DropdownHeader>
      </AddPropContainer>
      <MenuWrapper style={{ gap: 4 }}>
        {primaryRows.map((primaryRow, index) => (!primaryRowSwitch[primaryRow.name] ? (
          <HoverableMenuItem
            key={`first-${primaryRow.name}-${index}`}
            item={{
              label: primaryRow.name === '%pr' ? '%PR' : capWords(primaryRow.name.includes('intensity') ? 'Intensity' : primaryRow.name),
            }}
            onClick={(e) => handleMenuItemClick(primaryRow, e)}
          />
        ) : null))}
        <CustomDivider />
        <HoverableMenuItem
          key='about-properties'
          item={{
            label: 'About Properties',
            icon: 'info2',
          }}
          onClick={handleAboutPropertiesClick}
        />
      </MenuWrapper>
    </Menu>
  );

  return (
    <Dropdown
      trigger={['click']}
      overlay={menu}
      aria-haspopup='true'
      onVisibleChange={(visible) => {
        dispatch(setIsStaticAddColumnDropdownMenuOpen(visible));
      }}
    >
      <DropdownContainerStatic 
        onClick={(e) => {
          e.stopPropagation();
        }}
        tabIndex={-1}
      >
        <DropdownIcon padding={size} data-tooltip-id='dropdownIcon'>
          <IcomoonReact
            iconSet={iconSet}
            size={15}
            icon='plus'
            color='white'
          />
        </DropdownIcon>
        <ReactTooltip
          id='dropdownIcon'
          key='dropdownIcon'
          place='top'
          content='Add Property'
          style={{
            zIndex: 9999,
            backgroundColor: 'rgba(68, 68, 68, 1)',
            color: 'white',
            fontFamily: 'Nunito Sans',
            fontSize: '13px',
          }}
        />
      </DropdownContainerStatic>
    </Dropdown>
  );
};

StaticAddColumnDropdownContainer.propTypes = {
  size: PropTypes.number.isRequired,
  primaryRows: PropTypes.instanceOf(Array).isRequired,
  primaryRowSwitch: PropTypes.instanceOf(Object).isRequired,
  setPrimaryRowSwitch: PropTypes.func.isRequired,
  setIntensityUnit: PropTypes.func.isRequired,
  setPrimaryRowSwitchState: PropTypes.func.isRequired,
};

export default StaticAddColumnDropdownContainer;
